/**
 * FilterPanel — filter panel with basic field controls + advanced filters.
 *
 * Visually matches MasterCrudAdvancedSearch.tsx:
 * - Basic fields: `grid grid-cols-1 md:grid-cols-3 gap-4` (not a cramped flex-wrap row).
 * - Advanced filters: `<details>` with the same summary header (chevron + label + hint),
 *   a header row (# / Columna / Condición / Valor) above the condition rows, and a
 *   connector badge ("Y"/"O") rendered BETWEEN rows (not just once at the top).
 *
 * Layout:
 * 1. Basic row: one control per filterable field, in declaration order — text/number/date
 *    inputs, select for enum fields, and a plain Activo/Inactivo/Todos select for boolean
 *    fields (e.g. "Estado") — exactly like MasterCrudAdvancedSearch's `enumOptionsFor`.
 *    There is no separate hardcoded status control; a status filter is just a boolean field.
 * 2. Advanced filters panel (<details>, collapsible):
 *    - Connector selector: Y (AND) / O (OR), segmented pill buttons
 *    - Condition rows: [#N] [Campo ▼] [Operador ▼] [Valor] [×], with a connector badge
 *      between consecutive rows
 *    - "+ Agregar condición" button
 * 3. Chips row: visible when advanced conditions have values and panel is closed
 * 4. Action row: [Limpiar] [Buscar]
 *
 * @module components/toolbar/FilterPanel
 * @see FMPV-MVWC-E002-S001 I-005
 */
import React, { useState, useCallback, useMemo } from 'react';
import {
  Eraser, Search,
  ChevronDown, Plus, X,
} from 'lucide-react';
import { Button } from '../../../Button/Button';
import { LookupField } from '../../../LookupField/LookupField';
import type { LookupRecord } from '../../../LookupField/LookupField.types';
import type { MasterPatternViewDefinition, MasterPatternViewLookupConfig } from '../../types/definition.types';
import { getAllFields } from '../../engine/fieldEngine';
import type { ActiveFilter } from '../../store/types';
import { useFieldEngine } from '../../hooks/useFieldEngine';
import type { FilterOperator } from '../../types/definition.types';

export interface FilterPanelProps {
  definition: MasterPatternViewDefinition;
  activeFilters: ActiveFilter[];
  onSearch: (filters: ActiveFilter[]) => void;
  onClear: () => void;
  /** Translation function override — falls back to the current Spanish text when omitted. */
  t?: (key: string, defaultValue?: string) => string;
}

interface FilterValues {
  [field: string]: {
    value: string;
    valueTo?: string;
    operator: FilterOperator;
    booleanValue?: boolean | null;
  };
}

interface AdvCondition {
  id: string;
  field: string;
  operator: string;
  value: string;
  /** Human-readable label for lookup conditions (chip display) */
  displayLabel?: string;
}

function resolveDisplay(record: LookupRecord, cfg: MasterPatternViewLookupConfig): string {
  if (cfg.displayTemplate) {
    return cfg.displayTemplate.replace(/\{(\w+)\}/g, (_, k) => String(record[k] ?? ''));
  }
  if (cfg.displayValue) return String(record[cfg.displayValue] ?? '');
  return cfg.displayFields.map((f) => String(record[f] ?? '')).join(' ');
}

let _idSeq = 0;
function newId() { return `cond-${++_idSeq}`; }

/**
 * Reconstructs the panel's full state (basic values, advanced conditions, connector)
 * from `activeFilters` — the last filters actually searched. Needed because FilterPanel
 * is conditionally rendered (`{isFilterPanelOpen && <FilterPanel .../>}` in
 * MasterPatternView.tsx), so it fully unmounts on close and loses local state; without
 * this, reopening the panel always came back empty, even right after a search.
 *
 * `handleBuscar` marks an advanced search by including a `__connector` entry — its
 * presence (not `advOpen`, which is always reset to `false` after a search) is what
 * tells us which shape to rebuild.
 */
function buildInitialFilterState(activeFilters: ActiveFilter[]): {
  filterValues: FilterValues;
  advConditions: AdvCondition[];
  connector: 'AND' | 'OR';
} {
  const connectorEntry = activeFilters.find((f) => f.field === '__connector');

  if (connectorEntry) {
    const advConditions: AdvCondition[] = activeFilters
      .filter((f) => f.field !== '__connector' && !f.field.startsWith('__'))
      .map((f) => ({
        id: newId(),
        field: f.field,
        operator: f.operator,
        value: String(f.value ?? ''),
        // The lookup's friendly label isn't part of ActiveFilter, so it can't survive
        // a remount — the chip falls back to the raw value (id) for lookup fields.
        displayLabel: '',
      }));
    return {
      filterValues: {},
      advConditions,
      connector: (connectorEntry.value as 'AND' | 'OR') ?? 'AND',
    };
  }

  const filterValues: FilterValues = {};
  for (const f of activeFilters) {
    if (f.field.startsWith('__')) continue;
    filterValues[f.field] = {
      value: String(f.value ?? ''),
      operator: (f.operator as FilterOperator) ?? 'contains',
    };
  }
  return { filterValues, advConditions: [], connector: 'AND' };
}

// ── Shared styles — sized like MasterCrudAdvancedSearch.tsx (controlCls/condCtrlCls) ──
const inputClass =
  'w-full px-3 py-2 text-sm border border-border-primary dark:border-dark-border-primary rounded-md bg-white dark:bg-dark-bg-primary text-content-primary dark:text-dark-content-primary ' +
  'focus:outline-none focus:border-[#93d1fd] focus:ring-[3px] focus:ring-primary-500/10 transition-colors';
const selectClass =
  'px-2.5 py-1.5 text-sm border border-border-primary dark:border-dark-border-primary rounded-md bg-white dark:bg-dark-bg-primary text-content-primary dark:text-dark-content-primary ' +
  'outline-none focus:border-[#93d1fd] transition-colors cursor-pointer';
const labelClass = 'block text-[11px] font-semibold text-content-tertiary dark:text-dark-content-tertiary tracking-[0.3px] mb-1';

/**
 * Compact LookupField overrides injected via <style> tag.
 * Tailwind v4 breaks BEM double-underscore selectors in arbitrary variants,
 * so we inject plain CSS scoped via [data-testid="filter-panel"].
 */
const LOOKUP_COMPACT_CSS = `
[data-testid="filter-panel"] .lookup-field {
  gap: 0 !important;
}
[data-testid="filter-panel"] .lookup-field__trigger {
  height: 36px !important; padding: 8px 12px !important; font-size: 0.875rem !important;
}
[data-testid="filter-panel"] .lookup-field__placeholder,
[data-testid="filter-panel"] .lookup-field__selected-text {
  font-size: 0.875rem !important;
}
[data-testid="filter-panel"] .lookup-field__clear {
  width: 0.875rem !important; height: 0.875rem !important;
}
[data-testid="filter-panel"] .lookup-field__chevron {
  width: 0.875rem !important; height: 0.875rem !important;
}
[data-testid="filter-panel"] .lookup-field__search {
  height: 36px !important; padding: 8px 2rem !important; font-size: 0.875rem !important;
}
[data-testid="filter-panel"] .lookup-field__search-icon {
  width: 0.875rem !important; height: 0.875rem !important;
}
[data-testid="filter-panel"] .lookup-field__search-clear {
  width: 1rem !important; height: 1rem !important;
}
`;

export function FilterPanel({
  definition,
  activeFilters,
  onSearch,
  onClear,
  t,
}: FilterPanelProps) {
  const tt = useMemo(
    () => t ?? ((key: string, defaultValue?: string) => defaultValue ?? key),
    [t],
  );

  // ── Operator sets by data type ─────────────────────────────────────────────
  // Grouped in a single useMemo (keyed on `tt`) so the arrays and the lookup
  // function below share one stable reference per translation change, instead
  // of being recreated as fresh objects on every render.
  const { LOOKUP_OPS, getOpsForDataType } = useMemo(() => {
    const STRING_OPS = [
      { value: 'contains', label: tt('opContains', 'contiene') },
      { value: 'starts_with', label: tt('opStartsWith', 'empieza con') },
      { value: 'equals', label: tt('opEquals', 'es igual a') },
      { value: 'not_equals', label: tt('opNotEquals', 'no es igual a') },
    ];
    const NUMBER_OPS = [
      { value: 'equals', label: tt('opEquals', 'es igual a') },
      { value: 'not_equals', label: tt('opNotEquals', 'no es igual a') },
      { value: 'gt', label: tt('opGt', 'mayor que') },
      { value: 'gte', label: tt('opGte', 'mayor o igual a') },
      { value: 'lt', label: tt('opLt', 'menor que') },
      { value: 'lte', label: tt('opLte', 'menor o igual a') },
    ];
    const DATE_OPS = [
      { value: 'gte', label: tt('opFrom', 'desde') },
      { value: 'lte', label: tt('opTo', 'hasta') },
      { value: 'equals', label: tt('opEquals', 'es igual a') },
    ];
    const BOOLEAN_OPS = [
      { value: 'equals', label: tt('opEquals', 'es igual a') },
    ];
    const LOOKUP_OPS = [
      { value: 'equals', label: tt('opEquals', 'es igual a') },
    ];
    function getOpsForDataType(dataType: string) {
      if (dataType === 'number' || dataType === 'decimal') return NUMBER_OPS;
      if (dataType === 'date') return DATE_OPS;
      if (dataType === 'boolean') return BOOLEAN_OPS;
      return STRING_OPS;
    }
    return { STRING_OPS, NUMBER_OPS, DATE_OPS, BOOLEAN_OPS, LOOKUP_OPS, getOpsForDataType };
  }, [tt]);

  const { filterControls } = useFieldEngine(definition);
  const [initialFilterState] = useState(() => buildInitialFilterState(activeFilters));
  const [filterValues, setFilterValues] = useState<FilterValues>(initialFilterState.filterValues);

  // Advanced filters state — seeded from activeFilters so reopening the panel (it fully
  // unmounts on close) shows the same conditions/connector that were last searched.
  // advOpen stays false: this mirrors the state right after a search (handleBuscar
  // always collapses it), which is when the summary chips below are meant to show.
  const [advOpen, setAdvOpen] = useState(false);
  const [connector, setConnector] = useState<'AND' | 'OR'>(initialFilterState.connector);
  const [advConditions, setAdvConditions] = useState<AdvCondition[]>(initialFilterState.advConditions);

  const hasFilterableFields = filterControls.length > 0;

  const getFieldValue = (fieldName: string) =>
    filterValues[fieldName] ?? { value: '', operator: 'contains' as FilterOperator };

  const setFieldValue = useCallback((fieldName: string, value: string) => {
    setFilterValues((prev) => ({
      ...prev,
      [fieldName]: { ...getFieldValue(fieldName), ...prev[fieldName], value },
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFieldProp = useCallback((fieldName: string, prop: string, val: unknown) => {
    setFilterValues((prev) => ({
      ...prev,
      [fieldName]: { ...getFieldValue(fieldName), ...prev[fieldName], [prop]: val },
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Advanced conditions helpers ──────────────────────────────────────────

  const addCondition = useCallback(() => {
    const first = filterControls[0];
    const isFirstLookup = first?.inputType === 'lookup';
    const defaultOp = isFirstLookup
      ? 'equals'
      : getOpsForDataType(first?.dataType ?? 'string')[0]?.value ?? 'contains';
    setAdvConditions((prev) => [
      ...prev,
      { id: newId(), field: first?.fieldName ?? '', operator: defaultOp, value: '', displayLabel: '' },
    ]);
  }, [filterControls, getOpsForDataType]);

  const removeCondition = useCallback((id: string) => {
    setAdvConditions((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const updateCondition = useCallback(
    (id: string, patch: Partial<Omit<AdvCondition, 'id'>>) => {
      setAdvConditions((prev) =>
        prev.map((c) => {
          if (c.id !== id) return c;
          const updated = { ...c, ...patch };
          // Reset operator, value, and displayLabel when field changes
          if (patch.field && patch.field !== c.field) {
            const fc = filterControls.find((x) => x.fieldName === patch.field);
            const isNextLookup = fc?.inputType === 'lookup';
            updated.operator = isNextLookup
              ? 'equals'
              : getOpsForDataType(fc?.dataType ?? 'string')[0]?.value ?? 'contains';
            updated.value = '';
            updated.displayLabel = '';
          }
          return updated;
        }),
      );
    },
    [filterControls, getOpsForDataType],
  );

  const handleAdvToggle = useCallback((nextOpen: boolean) => {
    setAdvOpen(nextOpen);

    if (nextOpen) {
      // Switching to advanced mode → clear basic filters
      setFilterValues({});
      // Auto-add first condition when opening empty panel
      if (advConditions.length === 0) {
        const first = filterControls[0];
        const isFirstLookup = first?.inputType === 'lookup';
        const defaultOp = isFirstLookup
          ? 'equals'
          : getOpsForDataType(first?.dataType ?? 'string')[0]?.value ?? 'contains';
        setAdvConditions([{ id: newId(), field: first?.fieldName ?? '', operator: defaultOp, value: '', displayLabel: '' }]);
      }
    }
    // NOTA: antes, al cerrar (`else`), esto vaciaba advConditions/connector ("switching to
    // basic mode"). El <details> nativo dispara `toggle` cuando su atributo `open` cambia
    // — también cuando `handleBuscar` lo cierra programáticamente (`setAdvOpen(false)`) al
    // buscar. Ese wipe corría JUSTO DESPUÉS de que handleBuscar ya había leído advConditions
    // para el onSearch, borrando las condiciones recién buscadas antes de que el chip
    // "Filtros activos" llegara a pintarse. Ya no se limpia nada al cerrar: las condiciones
    // sobreviven tanto a un `handleBuscar` como a que el usuario colapse el acordeón a mano.
  }, [advConditions.length, filterControls, getOpsForDataType]);

  const activeAdvCount = advConditions.filter((c) => c.value.trim()).length;

  // ── Buscar / Limpiar ─────────────────────────────────────────────────────

  const handleBuscar = useCallback(() => {
    const basicFilters: ActiveFilter[] = filterControls
      .map((fc): ActiveFilter | null => {
        const fv = filterValues[fc.fieldName];
        if (!fv) return null;
        if (fc.dataType === 'boolean') {
          if (fv.booleanValue === null || fv.booleanValue === undefined) return null;
          return { field: fc.fieldName, operator: 'equals', value: fv.booleanValue };
        }
        if (!fv.value && !fv.valueTo) return null;
        return { field: fc.fieldName, operator: fv.operator ?? 'contains', value: fv.value };
      })
      .filter((f): f is ActiveFilter => f !== null);

    const activeAdv = advConditions.filter((c) => c.value.trim());
    const connectorFilter: ActiveFilter[] =
      activeAdv.length > 0
        ? [{ field: '__connector', operator: 'equals', value: connector }]
        : [];
    const advFilters: ActiveFilter[] = activeAdv.map((c) => ({
      field: c.field,
      operator: c.operator,
      value: c.value,
    }));

    const isAdvancedActive = advOpen || activeAdv.length > 0;
    setAdvOpen(false);
    onSearch([
      ...(isAdvancedActive ? [] : basicFilters),
      ...connectorFilter,
      ...advFilters,
    ]);
  }, [filterControls, filterValues, connector, advConditions, advOpen, onSearch]);

  const handleLimpiar = useCallback(() => {
    setFilterValues({});
    setAdvConditions([]);
    setConnector('AND');
    setAdvOpen(false);
    onClear();
  }, [onClear]);

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="px-5 py-3.5 bg-[#f8f9fc] dark:bg-zinc-900 border-b border-border-primary dark:border-dark-border-primary" data-testid="filter-panel">
      <style dangerouslySetInnerHTML={{ __html: LOOKUP_COMPACT_CSS }} />

      {/* ── Basic filter fields — grid, matches MasterCrudAdvancedSearch.tsx:124 ──── */}
      <div
        {...((advOpen || activeAdvCount > 0) ? { inert: true } : {})}
        className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 transition-opacity duration-200 ${(advOpen || activeAdvCount > 0) ? 'opacity-50 pointer-events-none' : ''}`}
      >
        {filterControls.map((fc) => {
          const fv = getFieldValue(fc.fieldName);

          // Boolean fields render as a plain Activo/Inactivo/Todos select — same
          // treatment as any other enum field, matching MasterCrudAdvancedSearch's
          // `enumOptionsFor`. There is no separate hardcoded "Estado" control.
          if (fc.dataType === 'boolean') {
            const boolStr = fv.booleanValue === true ? 'true' : fv.booleanValue === false ? 'false' : '';
            return (
              <div key={fc.fieldName} data-testid={`filter-${fc.fieldName}`}>
                <label className={labelClass}>{fc.label}</label>
                <select
                  value={boolStr}
                  onChange={(e) => setFieldProp(fc.fieldName, 'booleanValue', e.target.value === '' ? null : e.target.value === 'true')}
                  className={`${inputClass} cursor-pointer`}
                >
                  <option value="">{tt('all', 'Todos')}</option>
                  <option value="true">{tt('active', 'Activo')}</option>
                  <option value="false">{tt('inactive', 'Inactivo')}</option>
                </select>
              </div>
            );
          }

          if (fc.dataType === 'date') {
            return (
              <div key={fc.fieldName} data-testid={`filter-${fc.fieldName}`}>
                <label className={labelClass}>{fc.label} {tt('opFrom', 'desde')}</label>
                <input
                  type="date"
                  value={fv.value}
                  onChange={(e) => setFieldValue(fc.fieldName, e.target.value)}
                  className={inputClass}
                />
              </div>
            );
          }

          if (fc.inputType === 'select') {
            const field = getAllFields(definition).find((f) => f.fieldName === fc.fieldName);
            return (
              <div key={fc.fieldName} data-testid={`filter-${fc.fieldName}`}>
                <label className={labelClass}>{fc.label}</label>
                <select
                  value={fv.value}
                  onChange={(e) => setFieldValue(fc.fieldName, e.target.value)}
                  className={`${inputClass} cursor-pointer`}
                >
                  <option value="">{tt('all', 'Todos')}</option>
                  {(field?.options ?? []).map((opt) => (
                    <option key={opt.value} value={String(opt.value)}>{opt.label}</option>
                  ))}
                </select>
              </div>
            );
          }

          if (fc.dataType === 'number' || fc.dataType === 'decimal') {
            return (
              <div key={fc.fieldName} data-testid={`filter-${fc.fieldName}`}>
                <label className={labelClass}>{fc.label}</label>
                <input
                  type="number"
                  value={fv.value}
                  onChange={(e) => setFieldValue(fc.fieldName, e.target.value)}
                  placeholder={fc.label}
                  className={inputClass}
                />
              </div>
            );
          }

          // Lookup: render LookupField in the basic filter bar
          if (fc.inputType === 'lookup') {
            const fieldDef = getAllFields(definition).find((f) => f.fieldName === fc.fieldName);
            if (fieldDef?.lookupConfig) {
              return (
                <div key={fc.fieldName} data-testid={`filter-${fc.fieldName}`}>
                  <label className={labelClass}>{fc.label}</label>
                  <LookupField
                    entity={fieldDef.lookupConfig.entity}
                    fetcher={fieldDef.lookupConfig.fetcher}
                    displayFields={fieldDef.lookupConfig.displayFields}
                    displayTemplate={fieldDef.lookupConfig.displayTemplate}
                    displayValue={fieldDef.lookupConfig.displayValue}
                    filters={
                      fieldDef.lookupConfig.getFilters
                        ? fieldDef.lookupConfig.getFilters(
                            Object.fromEntries(
                              Object.entries(filterValues).map(([k, v]) => [k, v.value]),
                            ),
                          )
                        : fieldDef.lookupConfig.filters
                    }
                    minChars={fieldDef.lookupConfig.minChars}
                    value={fv.value || null}
                    onChange={(rec: LookupRecord | null) => {
                      setFieldValue(fc.fieldName, rec?.Id ?? '');
                      if (rec && fieldDef.lookupConfig?.onFilterSelect) {
                        fieldDef.lookupConfig.onFilterSelect(
                          rec as Record<string, unknown>,
                          (fieldName: string, val: unknown) =>
                            setFieldValue(fieldName, String(val ?? '')),
                        );
                      }
                    }}
                  />
                </div>
              );
            }
          }

          // Default: text
          return (
            <div key={fc.fieldName} data-testid={`filter-${fc.fieldName}`}>
              <label className={labelClass}>{fc.label}</label>
              <input
                type="text"
                value={fv.value}
                onChange={(e) => setFieldValue(fc.fieldName, e.target.value)}
                placeholder={fc.label}
                className={inputClass}
                data-testid={`filter-${fc.fieldName}-value`}
              />
            </div>
          );
        })}
      </div>

      {/* ── Filtros avanzados — <details>, misma estructura que MasterCrudAdvancedSearch.tsx:146 ── */}
      {hasFilterableFields && (
        <details
          className="mt-4 border border-border-primary dark:border-dark-border-primary rounded-lg overflow-hidden"
          open={advOpen}
          onToggle={(e) => handleAdvToggle((e.target as HTMLDetailsElement).open)}
        >
          <summary
            className="flex items-center gap-2 cursor-pointer select-none px-4 py-3 bg-white dark:bg-dark-bg-primary hover:bg-background-secondary dark:hover:bg-dark-bg-secondary transition-colors"
            style={{ listStyle: 'none' }}
            data-testid="filter-adv-toggle"
          >
            <ChevronDown className={`w-4 h-4 text-primary-600 flex-shrink-0 transition-transform duration-200 ${advOpen ? 'rotate-180' : ''}`} />
            <span className="text-sm font-semibold text-[#1355b7] dark:text-primary-400">{tt('advancedFilters', 'Filtros avanzados')}</span>
            <span className="hidden sm:inline text-xs text-content-tertiary font-normal">{tt('advancedFiltersHint', '— combina varias condiciones personalizadas')}</span>
            {activeAdvCount > 0 && (
              <span className="bg-primary-600 text-white rounded-full px-1.5 text-[10px] font-bold leading-[18px]">
                {activeAdvCount}
              </span>
            )}
          </summary>

          <div className="border-t border-border-primary dark:border-dark-border-primary bg-background-secondary dark:bg-dark-bg-secondary px-4 py-4 flex flex-col gap-4" data-testid="adv-filters-panel">
            {/* Conector lógico global (Y/O) — segmented pills, igual al prototipo */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <span className="text-[11px] font-semibold text-content-secondary tracking-[0.3px] uppercase">
                {tt('joinConditionsWith', 'Unir condiciones con')}
              </span>
              <div role="radiogroup" aria-label={tt('joinConditionsWith', 'Unir condiciones con')} className="inline-flex rounded-md border border-border-primary dark:border-dark-border-primary overflow-hidden bg-white dark:bg-dark-bg-primary shadow-[0_1px_2px_0_rgba(16,24,40,0.05)]">
                {([
                  { v: 'AND' as const, label: tt('connectorAndLabel', 'Y') },
                  { v: 'OR' as const, label: tt('connectorOrLabel', 'O') },
                ]).map((opt, i) => {
                  const active = connector === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setConnector(opt.v)}
                      className={`px-4 py-1.5 text-sm font-semibold transition-colors ${i === 1 ? 'border-l border-border-primary dark:border-dark-border-primary' : ''} ${active ? 'bg-primary-600 text-white' : 'bg-white dark:bg-dark-bg-primary text-content-secondary hover:bg-background-secondary dark:hover:bg-dark-bg-secondary'}`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <span className="text-xs text-content-tertiary">
                {connector === 'AND'
                  ? tt('connectorAndHint', 'Se deben cumplir todas las condiciones')
                  : tt('connectorOrHint', 'Basta con que se cumpla una condición')}
              </span>
            </div>

            {/* Tarjeta de condiciones */}
            <div className="rounded-lg bg-white dark:bg-dark-bg-primary overflow-hidden shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] ring-1 ring-black/[0.04]">
              <div className="hidden md:flex items-center gap-2 px-3 py-2 bg-white dark:bg-dark-bg-primary border-b border-border-primary dark:border-dark-border-primary text-[11px] font-semibold text-content-secondary tracking-[0.3px] uppercase">
                <span className="w-5 flex-shrink-0 text-center">#</span>
                <span className="w-[170px]">{tt('column', 'Columna')}</span>
                <span className="w-[150px]">{tt('condition', 'Condición')}</span>
                <span className="flex-1 min-w-0">{tt('value', 'Valor')}</span>
                <span className="w-8 flex-shrink-0" />
              </div>

              {advConditions.map((cond, idx) => {
                const fc = filterControls.find((c) => c.fieldName === cond.field);
                const isLookup = fc?.inputType === 'lookup';
                const ops = isLookup ? LOOKUP_OPS : getOpsForDataType(fc?.dataType ?? 'string');
                const fieldDef = getAllFields(definition).find((f) => f.fieldName === cond.field);
                const isSelect = fc?.inputType === 'select';
                const isBoolean = fc?.dataType === 'boolean';
                const isDate = fc?.dataType === 'date';
                const isNumber = fc?.dataType === 'number' || fc?.dataType === 'decimal';

                return (
                  <React.Fragment key={cond.id}>
                    <div className="flex flex-col md:flex-row md:items-center gap-2 px-3 py-2.5" data-testid={`adv-condition-${idx}`}>
                      <span className="w-5 flex-shrink-0 text-center text-xs font-bold text-content-tertiary select-none">{idx + 1}</span>

                      <select
                        value={cond.field}
                        onChange={(e) => updateCondition(cond.id, { field: e.target.value })}
                        className={`${selectClass} w-full md:w-[170px]`}
                        data-testid={`adv-condition-${idx}-field`}
                      >
                        {filterControls.map((f) => (
                          <option key={f.fieldName} value={f.fieldName}>{f.label}</option>
                        ))}
                      </select>

                      <select
                        value={cond.operator}
                        onChange={(e) => updateCondition(cond.id, { operator: e.target.value })}
                        className={`${selectClass} w-full md:w-[150px]`}
                        data-testid={`adv-condition-${idx}-operator`}
                      >
                        {ops.map((op) => (
                          <option key={op.value} value={op.value}>{op.label}</option>
                        ))}
                      </select>

                      {isLookup && fieldDef?.lookupConfig ? (
                        <div className="w-full md:flex-1 md:min-w-0" data-testid={`adv-condition-${idx}-value`}>
                          <LookupField
                            entity={fieldDef.lookupConfig.entity}
                            fetcher={fieldDef.lookupConfig.fetcher}
                            displayFields={fieldDef.lookupConfig.displayFields}
                            displayTemplate={fieldDef.lookupConfig.displayTemplate}
                            displayValue={fieldDef.lookupConfig.displayValue}
                            filters={
                              fieldDef.lookupConfig.getFilters
                                ? fieldDef.lookupConfig.getFilters(
                                    Object.fromEntries(
                                      advConditions
                                        .filter((c) => c.value)
                                        .map((c) => [c.field, c.value]),
                                    ),
                                  )
                                : fieldDef.lookupConfig.filters
                            }
                            minChars={fieldDef.lookupConfig.minChars}
                            value={cond.value || null}
                            onChange={(rec: LookupRecord | null) => updateCondition(cond.id, {
                              value: rec?.Id ?? '',
                              displayLabel: rec && fieldDef.lookupConfig
                                ? resolveDisplay(rec, fieldDef.lookupConfig)
                                : '',
                            })}
                          />
                        </div>
                      ) : isSelect ? (
                        <select
                          value={cond.value}
                          onChange={(e) => updateCondition(cond.id, { value: e.target.value })}
                          className={`${selectClass} w-full md:flex-1 md:min-w-0`}
                          data-testid={`adv-condition-${idx}-value`}
                        >
                          <option value="">{tt('selectPlaceholder', 'Seleccionar...')}</option>
                          {(fieldDef?.options ?? []).map((opt) => (
                            <option key={opt.value} value={String(opt.value)}>{opt.label}</option>
                          ))}
                        </select>
                      ) : isBoolean ? (
                        <select
                          value={cond.value}
                          onChange={(e) => updateCondition(cond.id, { value: e.target.value })}
                          className={`${selectClass} w-full md:flex-1 md:min-w-0`}
                          data-testid={`adv-condition-${idx}-value`}
                        >
                          <option value="">{tt('selectPlaceholder', 'Seleccionar...')}</option>
                          <option value="true">{tt('yes', 'Sí')}</option>
                          <option value="false">{tt('no', 'No')}</option>
                        </select>
                      ) : (
                        <input
                          type={isDate ? 'date' : isNumber ? 'number' : 'text'}
                          value={cond.value}
                          onChange={(e) => updateCondition(cond.id, { value: e.target.value })}
                          placeholder={tt('valuePlaceholder', 'Valor...')}
                          className={`${selectClass} w-full md:flex-1 md:min-w-0`}
                          data-testid={`adv-condition-${idx}-value`}
                        />
                      )}

                      <button
                        type="button"
                        onClick={() => removeCondition(cond.id)}
                        title={tt('removeConditionTitle', 'Eliminar condición')}
                        aria-label={tt('removeConditionTitle', 'Eliminar condición')}
                        className="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 self-end md:self-auto rounded-md text-content-tertiary hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        data-testid={`adv-condition-${idx}-remove`}
                      >
                        <X size={16} />
                      </button>
                    </div>

                    {/* Conector entre condiciones — refleja el selector global Y/O */}
                    {idx < advConditions.length - 1 && (
                      <div className="flex items-center gap-2 px-3">
                        <span className="ml-5 inline-flex items-center justify-center h-5 min-w-[26px] px-2 rounded-full text-[10px] font-bold tracking-wide bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-100 dark:border-primary-800 select-none">
                          {connector === 'AND' ? tt('connectorAndLabel', 'Y') : tt('connectorOrLabel', 'O')}
                        </span>
                        <span className="h-px flex-1 bg-border-primary/70 dark:bg-dark-border-primary/70" />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            <div>
              <button
                type="button"
                onClick={addCondition}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold text-primary-600 border border-dashed border-[#93d1fd] hover:bg-[#eff8ff] dark:hover:bg-primary-900/20 transition-colors"
                data-testid="adv-add-condition"
              >
                <Plus size={14} />
                {tt('addConditionLabel', 'Agregar condición')}
              </button>
            </div>
          </div>
        </details>
      )}

      {/* ── Chips: active advanced conditions when panel is closed ─────────── */}
      {!advOpen && activeAdvCount > 0 && (
        <div className="flex items-center flex-wrap gap-1.5 pt-2.5" data-testid="adv-chips">
          <span className="text-[11px] text-content-tertiary dark:text-dark-content-tertiary font-semibold">{tt('activeFiltersLabel', 'Filtros activos:')}</span>
          {advConditions
            .filter((c) => c.value.trim())
            .map((c) => {
              const fc = filterControls.find((x) => x.fieldName === c.field);
              return (
                <span
                  key={c.id}
                  className="inline-flex items-center gap-1.5 bg-[#eff8ff] border border-[#93d1fd] rounded-md px-2.5 py-0.5 text-[11px] text-[#1355b7]"
                >
                  {fc?.label ?? c.field}:{' '}
                  {fc?.inputType === 'lookup'
                    ? (c.displayLabel || c.value)
                    : fc?.dataType === 'boolean'
                      ? (c.value === 'true' ? tt('yes', 'Sí') : c.value === 'false' ? tt('no', 'No') : c.value)
                      : c.value}
                </span>
              );
            })}
          <button
            type="button"
            onClick={handleLimpiar}
            className="text-[11px] text-content-tertiary dark:text-dark-content-tertiary underline cursor-pointer bg-transparent border-none p-0"
          >
            {tt('clearAdvancedFilters', 'Limpiar filtros avanzados')}
          </button>
        </div>
      )}

      {/* ── Limpiar / Buscar — siempre al fondo del panel ─────────────────── */}
      <div className="flex items-center justify-end gap-2 mt-3.5">
        <Button
          type="outline"
          size="base"
          className="!h-9 !px-3 !text-sm font-medium"
          leftIcon={<Eraser size={14} />}
          onClick={handleLimpiar}
          data-testid="filter-panel-clear"
        >
          {tt('clear', 'Limpiar')}
        </Button>
        <Button
          type="default"
          size="base"
          className="!h-9 !px-3 !text-sm font-medium"
          leftIcon={<Search size={14} />}
          onClick={handleBuscar}
          data-testid="filter-panel-search"
        >
          {tt('search', 'Buscar')}
        </Button>
      </div>
    </div>
  );
}
